/* eslint-disable indent */
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
    // Box,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    DialogActions,
    // Stack,
} from "@mui/material";
import noop from "lodash-es/noop";
import "./ScanConfirmationDialog.scss";
import { formatDialogJson } from "./hooks/formatDialogJson";

export const SCAN_DIALOG_DATA_PROPTYPES = {
    choiceName: PropTypes.string.isRequired,
    titleText: PropTypes.string,
    dialogText: PropTypes.string,
    dialogJson: PropTypes.object,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
        }).isRequired,
    ),
};

/**
 * Displays workflow-controlled confirmation dialog prompting user to make a decision.
 * @return {*}
 */
const ScanConfirmationDialog = ({
    choiceName = "NONE",
    titleText,
    dialogJson,
    dialogText,
    options = [],
    onChoiceSubmitted = noop,
    onClose = noop,
    status = {
        modalOpen: false,
        buttonsActive: false,
        name: "",
        message: "",
    },
}) => {
    const {
        modalOpen = false,
        buttonsActive = false,
        name: statusName = "",
        message: statusMessage = "",
    } = status;

    // If we have a json as ouput we use the PUT method to send the whole text output as body for logging in the server.
    const dynamicTextResponse = useMemo(() => {
        if (!dialogJson) {
            return null;
        }

        const result = {
            choice: null,
            text: dialogText || "",
            textList: [],
        };

        if (dialogText) {
            try {
                result.textList = dialogText.split("\n");
            } catch (e) {
                console.error("Failed to split dialog text:", e);
                result.textList = [];
            }
        }

        return result;
    }, [dialogJson, dialogText]);

    /**
     * @param   {object} event
     * @return {*}
     */
    const handleChoice = (event) =>
        onChoiceSubmitted(event.target.dataset.choice, dynamicTextResponse);

    return (
        <Dialog
            open={modalOpen}
            onClose={onClose}
            className="ScanConfirmationDialog"
            sx={{
                "& .MuiPaper-root": {
                    maxHeight: "calc(100vh - 64px)",
                    minWidth: "50vw",
                },
            }}
        >
            {titleText && <DialogTitle>{titleText}</DialogTitle>}
            <DialogContent>
                {dialogJson
                    ? formatDialogJson(dialogText, dialogJson)
                    : dialogText && <p>{dialogText}</p>}

                {statusMessage && (
                    <p className={`status ${statusName || ""}`}>
                        {statusMessage}
                    </p>
                )}
            </DialogContent>
            <DialogActions
                sx={{ justifyContent: "space-between", px: 3, pb: 2 }}
            >
                {options.map((option) => (
                    <Button
                        variant="contained"
                        key={option.name}
                        id={`choice-${choiceName}-${option.name}`}
                        disabled={!buttonsActive}
                        onClick={handleChoice}
                        data-choice={option.name}
                        color={option.color}
                    >
                        {option.text}
                    </Button>
                ))}
            </DialogActions>
        </Dialog>
    );
};

/*
     Example JSON:
      {
            "title":"",
            "lines" : [
                { "table":["Produktname", "UN-Nummer", "Klasse", "Verpackungsgruppe"],"data":[][]},
                { "nl": 1, "value":"<b>Vorgesehenes Gefahrgutrecht:</b> __ADRREGULATION__"},
                { "nl": 2, "value":"Erklärungen zum eingesetzten Tankcontainer / ortsbewegliche Tank mit der Nummer:"},
                { "nl": 1, "value":"__LICENCEPLATENUMBER__ <i>(=Containernummer from WCI)</i>", "style":{"textAlign": "center"}},
                { "table":["Produktname1", "UN-Nummer1", "Klasse1", "Verpackungsgruppe1"],"data":[][]},
                { "nl": 1, "value":"Ich erkläre, dass"},
                { "value":"- &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;der Tankcontainer / ortsbewegliche Tank mit den aufgeführten Tankcodes  ADR: __ADR__  IMDG: __IMDG__ gekennzeichnet ist."},
                { "value":"- &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;der Tankcontainer / ortsbewegliche Tank den gesetzlichen <b>Transportanforderungen</b> nach __TYPE__ entspricht."},
                { "value":"- &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;die geforderten Sondervorschriften erfüllt sind."},
                { "value":"- &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;die gesetzlich vorgeschriebenen <b>Prüffristen</b> nicht abgelaufen sind."},
                { "value":"- &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;die verwendeten <b>Dichtungen</b> den Materialanforderungen entsprechen."},
                { "nl": 2, "value":"DSGVO: Wir weisen darauf hin, dass diese Erklärung mindestens 10 Jahre bei uns aufbewahrt wird."},
                { "nl": 3, "value":"Datum: __DATE__"},
                { "value":"Name, Vorname: __NAME__"},
                { "value":"Firma: i.A. __COMPANY__"},
                { "nl": 3}
            ]
        }

         - "nl":2 Adds previous 2 new lines
         - "table": Headernames and data:[][] 2-dim data
         - "value": is the value per line

         - "- &nbsp;": The combination with whitespace is used for FE and the log output so the tabs are right in the FE
         - FE: we send this exact JSON to the FE but w/ the already replaced values, so fixed output, only "table" gets an data=[][] element with the values to be shown.
           The log output is send too in the text field to the FE, it is send back to the REST API to use for the logging, the log output uses "\n" per line and is split to List<String>
         - HTML FE we use "html-react-parser", e.g. <b></b>, <i></i> etc
*/

ScanConfirmationDialog.propTypes = {
    ...SCAN_DIALOG_DATA_PROPTYPES,
    status: PropTypes.shape({
        name: PropTypes.string.isRequired,
        message: PropTypes.string,
        modalOpen: PropTypes.bool,
        buttonsActive: PropTypes.bool,
    }).isRequired,
    onClose: PropTypes.func,
    onChoiceSubmitted: PropTypes.func,
};

export default ScanConfirmationDialog;
